import { useState, useRef, useEffect, useContext, useCallback } from "react";
import PricingModal from "../pricingModal";
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import SendIcon from '@mui/icons-material/SendRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddIcon from '@mui/icons-material/AddRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopyRounded';
import CheckIcon from '@mui/icons-material/CheckRounded';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAddRounded';
import BookmarkIcon from '@mui/icons-material/BookmarkRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineRounded';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightIcon from '@mui/icons-material/ChevronRightRounded';
import AttachFileIcon from '@mui/icons-material/AttachFileRounded';
import CloseIcon from '@mui/icons-material/CloseRounded';
import DownloadIcon from '@mui/icons-material/DownloadRounded';
import DescriptionIcon from '@mui/icons-material/DescriptionRounded';
import TableChartIcon from '@mui/icons-material/TableChartRounded';
import SlideshowIcon from '@mui/icons-material/SlideshowRounded';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdfRounded';
import ImageIcon from '@mui/icons-material/ImageRounded';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFileRounded';
import { useTranslation } from 'react-i18next';

import { AuthContext } from "../../contexts/auth";
import api from "../../services/api";

const SYSTEM_PROMPT = `Sei Admind AI, l'assistente marketing di Admind — piattaforma SaaS italiana per il marketing AI.

Il tuo carattere:
- Sei entusiasta, caldo e amichevole — come un amico esperto che vuole davvero aiutarti 🤗
- Usi emoji in modo naturale per rendere la conversazione vivace ✨
- Parli sempre in italiano, con un tono colloquiale ma professionale
- Sei curioso e fai domande per capire meglio come aiutare

La tua expertise:
- Marketing digitale, content strategy, copywriting, social media, SEO
- Capisci il business dell'utente e dai consigli pratici e azionabili
- Puoi leggere e analizzare documenti, Excel, immagini allegati dall'utente

═══════════════════════════════════════════
SISTEMA DI EXPORT DOCUMENTI — REGOLA ASSOLUTA
═══════════════════════════════════════════
La piattaforma Admind converte AUTOMATICAMENTE il testo Markdown delle tue risposte in file Word, Excel, PowerPoint e PDF tramite un sistema di export integrato. L'utente ha un pulsante ⬇ accanto ad ogni tua risposta per scaricare il file nel formato che preferisce.

Questo significa: tu NON generi file binari — scrivi testo strutturato in Markdown, e il sistema fa la conversione. Puoi SEMPRE creare qualsiasi documento.

QUANDO L'UTENTE CHIEDE UNA PRESENTAZIONE / PPT / SLIDE:
→ Rispondi subito con il contenuto completo strutturato così:

# [Titolo della presentazione]
> [Sottotitolo o descrizione breve]

## [Titolo slide 1]
- Punto chiave 1
- Punto chiave 2
- Punto chiave 3

## [Titolo slide 2]
- Punto chiave 1
- Punto chiave 2

[continua per tutte le slide richieste]

Poi scrivi: "Ecco la tua presentazione! 🎉 Clicca il pulsante ⬇ accanto a questo messaggio e scegli **PowerPoint** per scaricarla."

QUANDO L'UTENTE CHIEDE UN DOCUMENTO WORD / REPORT / ARTICOLO:
→ Scrivi il contenuto completo con # titoli, ## sezioni, **grassetto**, - elenchi
→ Poi: "Clicca ⬇ e scegli **Word** per scaricarlo."

QUANDO L'UTENTE CHIEDE UN FOGLIO EXCEL / TABELLA:
→ Scrivi tabelle Markdown con | colonne | separate | da | pipe |
→ Poi: "Clicca ⬇ e scegli **Excel** per scaricarlo."

REGOLA FONDAMENTALE: Non dire MAI "non posso creare file" o "non ho questa funzione". Quella risposta è SBAGLIATA. Scrivi sempre il contenuto e indica il pulsante ⬇ per il download.
═══════════════════════════════════════════

Ricorda: ogni risposta deve far sentire l'utente capito, supportato e motivato! 💜`;

const MODELS = {
    gpt: {
        id: 'gpt',
        label: 'GPT-5.4',
        model: 'gpt-5.4',
        provider: 'openai',
        color: '#10A37F',
        bgColor: 'rgba(16,163,127,0.1)',
        borderColor: 'rgba(16,163,127,0.25)',
        icon: '⚡',
    },
    claude: {
        id: 'claude',
        label: 'Opus 4-6',
        model: 'claude-opus-4-5',
        provider: 'claude',
        color: '#D4780C',
        bgColor: 'rgba(212,120,12,0.1)',
        borderColor: 'rgba(212,120,12,0.25)',
        icon: '✦',
    },
};

const EXPORT_FORMATS = [
    { id: 'word', label: 'Word (.docx)', icon: <DescriptionIcon sx={{ fontSize: '1rem', color: '#2B579A' }} /> },
    { id: 'excel', label: 'Excel (.xlsx)', icon: <TableChartIcon sx={{ fontSize: '1rem', color: '#217346' }} /> },
    { id: 'pptx', label: 'PowerPoint (.pptx)', icon: <SlideshowIcon sx={{ fontSize: '1rem', color: '#D24726' }} /> },
    { id: 'pdf', label: 'PDF (.pdf)', icon: <PictureAsPdfIcon sx={{ fontSize: '1rem', color: '#E44D26' }} /> },
];

const ACCEPTED_TYPES = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.webp,.gif';

const fileIcon = (type) => {
    if (type === 'image') return <ImageIcon sx={{ fontSize: '0.85rem' }} />;
    if (type === 'spreadsheet') return <TableChartIcon sx={{ fontSize: '0.85rem' }} />;
    return <InsertDriveFileIcon sx={{ fontSize: '0.85rem' }} />;
};

const MarkdownText = ({ text }) => {
    const lines = (text || '').split('\n');
    const elements = [];
    lines.forEach((line, i) => {
        const inlineHtml = line
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            .replace(/`(.*?)`/g, '<code style="background:rgba(112,18,128,0.12);padding:2px 6px;border-radius:5px;font-size:0.85em;font-family:monospace;color:#8E3A9E">$1</code>');
        elements.push(
            <span key={i} dangerouslySetInnerHTML={{ __html: inlineHtml }} />,
            i < lines.length - 1 ? <br key={`br-${i}`} /> : null
        );
    });
    return <span style={{ lineHeight: 1.7 }}>{elements}</span>;
};

const CopyButton = ({ text }) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    return (
        <Tooltip title={copied ? t('chat.copy_copied') : t('chat.copy')}>
            <IconButton size="small" onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                sx={{ color: copied ? '#10B981' : '#9B9BA8', transition: 'color 0.2s', p: 0.5 }}>
                {copied ? <CheckIcon sx={{ fontSize: '0.85rem' }} /> : <ContentCopyIcon sx={{ fontSize: '0.85rem' }} />}
            </IconButton>
        </Tooltip>
    );
};

const ExportButton = ({ text }) => {
    const { t } = useTranslation();
    const [anchor, setAnchor] = useState(null);
    const [exporting, setExporting] = useState(null);

    const handleExport = async (fmt) => {
        setAnchor(null);
        setExporting(fmt);
        try {
            const resp = await api.post('/api/openai/chat-export/', {
                content: text,
                format: fmt,
                filename: 'admind-chat',
            }, { responseType: 'blob' });
            const ext = fmt === 'word' ? 'docx' : fmt === 'pptx' ? 'pptx' : fmt === 'excel' ? 'xlsx' : 'pdf';
            const url = window.URL.createObjectURL(new Blob([resp.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `admind-chat.${ext}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Export error', e);
        }
        setExporting(null);
    };

    return (
        <>
            <Tooltip title={t('chat.export_tooltip')}>
                <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}
                    sx={{ color: exporting ? '#701280' : '#9B9BA8', p: 0.5, transition: 'color 0.2s' }}>
                    {exporting ? <CircularProgress size={12} sx={{ color: '#701280' }} /> : <DownloadIcon sx={{ fontSize: '0.85rem' }} />}
                </IconButton>
            </Tooltip>
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
                PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid rgba(0,0,0,0.07)', minWidth: 190 } }}>
                <Box sx={{ px: 1.5, py: 0.8 }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#9B9BA8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {t('chat.export_label')}
                    </Typography>
                </Box>
                {EXPORT_FORMATS.map(f => (
                    <MenuItem key={f.id} onClick={() => handleExport(f.id)}
                        sx={{ fontSize: '0.82rem', color: '#1C1C28', borderRadius: '8px', mx: 0.5, py: 0.8, '&:hover': { background: 'rgba(112,18,128,0.07)' } }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>{f.icon}</ListItemIcon>
                        {f.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

const SIDEBAR_W = 260;

const ModernChat = () => {
    const { t } = useTranslation();
    const { user } = useContext(AuthContext);
    const [activeModelId, setActiveModelId] = useState('gpt');
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [savedSessions, setSavedSessions] = useState([]);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [saveName, setSaveName] = useState('');
    const [saving, setSaving] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);

    const currentPlanKey = user?.subscription === 'PRO Max'
        ? 'pro_max'
        : user?.subscription === 'PRO Light'
            ? 'pro_light'
            : null;
    const hasProMaxAccess = currentPlanKey === 'pro_max';

    const SUGGESTIONS = t('chat.suggestions', { returnObjects: true });

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    const loadSessions = useCallback(async () => {
        setSessionsLoading(true);
        try {
            const resp = await api.get('/api/openai/chat-sessions/');
            setSavedSessions(resp.data.filter(s => s.system !== 'agent'));
        } catch (e) { }
        setSessionsLoading(false);
    }, []);

    useEffect(() => { loadSessions(); }, [loadSessions]);

    const activeModel = MODELS[activeModelId];

    const switchModel = (modelId) => {
        if (modelId === activeModelId) return;
        if (modelId === 'claude' && !hasProMaxAccess) {
            setShowUpgradeModal(true);
            return;
        }
        setActiveModelId(modelId);
        setMessages([]);
        setInput('');
        setCurrentSessionId(null);
        setAttachments([]);
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        e.target.value = '';
        setUploading(true);
        const newAttachments = [];
        for (const file of files) {
            try {
                const fd = new FormData();
                fd.append('file', file);
                const resp = await api.post('/api/openai/chat-upload/', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                newAttachments.push({ ...resp.data, originalName: file.name });
            } catch (err) {
                newAttachments.push({ type: 'error', name: file.name, originalName: file.name, error: t('chat.upload_error') });
            }
        }
        setAttachments(prev => [...prev, ...newAttachments]);
        setUploading(false);
    };

    const removeAttachment = (idx) => {
        setAttachments(prev => prev.filter((_, i) => i !== idx));
    };

    const handleSend = async (textOverride) => {
        const text = textOverride || input.trim();
        if ((!text && attachments.length === 0) || loading) return;

        let userContent = text;
        const images = [];
        const docParts = [];

        for (const att of attachments) {
            if (att.type === 'image') {
                images.push({ data_url: att.data_url, name: att.name });
            } else if (att.content) {
                docParts.push(`[Allegato: ${att.name}]\n${att.content}`);
            }
        }

        if (docParts.length > 0) {
            const docContext = docParts.join('\n\n---\n\n');
            userContent = userContent
                ? `${userContent}\n\n${docContext}`
                : docContext;
        }

        const userMessage = {
            role: 'user',
            content: userContent,
            images: images.length > 0 ? images : undefined,
            _attachments: attachments.map(a => ({ name: a.name, type: a.type })),
            _displayText: text || (attachments.map(a => a.name).join(', ')),
        };

        const newMessages = [...messages, userMessage];
        setMessages(newMessages);
        setInput('');
        setAttachments([]);
        setLoading(true);

        const apiMessages = newMessages.map(m => ({
            role: m.role,
            content: m.content || '',
            images: m.images,
        }));

        const streamId = Date.now();
        let firstChunk = true;
        let streamedContent = '';

        try {
            const csrfToken = document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '';
            const authHeader = api.defaults?.headers?.common?.['Authorization'] || '';
            const response = await fetch('/api/openai/chatv2/stream/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                    ...(authHeader ? { 'Authorization': authHeader } : {}),
                },
                credentials: 'include',
                body: JSON.stringify({
                    messages: apiMessages,
                    provider: activeModel.provider,
                    model: activeModel.model,
                    system_prompt: SYSTEM_PROMPT,
                }),
            });

            if (!response.ok) {
                let errData = {};
                try { errData = await response.json(); } catch (_) {}
                if (response.status === 402) {
                    window.dispatchEvent(new CustomEvent('admind_limit_exceeded', {
                        detail: {
                            code: errData.code || 'free_limit_exceeded',
                            limit_type: errData.limit_type || 'tokens',
                            has_stripe_customer: errData.has_stripe_customer === true,
                            current_plan: errData.current_plan || null,
                        }
                    }));
                }
                if (response.status === 403 && errData.code === 'pro_max_required') {
                    setShowUpgradeModal(true);
                }
                throw new Error(errData.error || t('chat.error_default'));
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    try {
                        const data = JSON.parse(line.slice(6));
                        if (data.chunk) {
                            if (firstChunk) {
                                firstChunk = false;
                                setLoading(false);
                                setMessages(prev => [...prev, { role: 'assistant', content: '', _streamId: streamId }]);
                            }
                            streamedContent += data.chunk;
                            setMessages(prev => prev.map(m =>
                                m._streamId === streamId ? { ...m, content: streamedContent } : m
                            ));
                        } else if (data.done) {
                            setMessages(prev => prev.map(m =>
                                m._streamId === streamId ? { ...m, _streamId: undefined } : m
                            ));
                        } else if (data.error) {
                            throw new Error(data.error);
                        }
                    } catch (parseErr) {
                        if (parseErr.message !== 'Unexpected token') {
                            throw parseErr;
                        }
                    }
                }
            }
        } catch (err) {
            const errMsg = err?.message || t('chat.error_default');
            setMessages(prev => {
                const withoutStream = prev.filter(m => m._streamId !== streamId);
                return [...withoutStream, { role: 'assistant', content: `❌ ${errMsg}`, isError: true }];
            });
        }
        setLoading(false);
    };

    const handleNewChat = () => {
        setMessages([]);
        setInput('');
        setCurrentSessionId(null);
        setAttachments([]);
    };

    const handleOpenSave = () => {
        const preview = messages.find(m => m.role === 'user')?.content || '';
        setSaveName(preview.slice(0, 40) || t('chat.save_dialog_default'));
        setSaveDialogOpen(true);
    };

    const handleSaveChat = async () => {
        if (!saveName.trim() || messages.length === 0) return;
        setSaving(true);
        try {
            const cleanMessages = messages.map(m => {
                const msg = { role: m.role, content: m.content || '' };
                const meta = {};
                if (m.images && m.images.length > 0) meta.images = m.images;
                if (m._attachments && m._attachments.length > 0) meta._attachments = m._attachments;
                if (m._displayText) meta._displayText = m._displayText;
                if (m.isError) meta.isError = true;
                if (Object.keys(meta).length > 0) msg.metadata = meta;
                return msg;
            });
            const resp = await api.post('/api/openai/chat-sessions/', {
                name: saveName.trim(),
                messages: cleanMessages,
                system: 'chat',
            });
            setCurrentSessionId(resp.data.id);
            setSaveDialogOpen(false);
            await loadSessions();
        } catch (e) { }
        setSaving(false);
    };

    const handleLoadSession = async (session) => {
        try {
            const resp = await api.get(`/api/openai/chat-sessions/${session.id}/`);
            const restored = resp.data.messages.map(m => {
                let meta = {};
                if (m.metadata) {
                    try { meta = typeof m.metadata === 'string' ? JSON.parse(m.metadata) : m.metadata; } catch (_) {}
                }
                return { ...m, ...meta };
            });
            setMessages(restored);
            setCurrentSessionId(session.id);
            setAttachments([]);
        } catch (e) { }
    };

    const handleDeleteSession = async (e, sessionId) => {
        e.stopPropagation();
        try {
            await api.delete(`/api/openai/chat-sessions/${sessionId}/`);
            if (currentSessionId === sessionId) { setMessages([]); setCurrentSessionId(null); }
            await loadSessions();
        } catch (e) { }
    };

    const isEmpty = messages.length === 0 && attachments.length === 0;

    return (
        <Box sx={{ height: '100%', display: 'flex', overflow: 'hidden', backgroundColor: '#FAFAF8' }}>
            <input ref={fileInputRef} type="file" multiple accept={ACCEPTED_TYPES} onChange={handleFileChange} style={{ display: 'none' }} />

            {/* Sidebar */}
            <Box sx={{
                width: sidebarOpen ? SIDEBAR_W : 0,
                minWidth: sidebarOpen ? SIDEBAR_W : 0,
                transition: 'all 0.25s ease',
                overflow: 'hidden',
                borderRight: '1px solid rgba(0,0,0,0.07)',
                background: '#fff',
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0,
            }}>
                <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BookmarkIcon sx={{ fontSize: '1rem', color: '#701280' }} />
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1C1C28', flex: 1 }}>{t('chat.saved_chats')}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#9B9BA8' }}>{savedSessions.length}</Typography>
                </Box>
                <Box sx={{ flex: 1, overflowY: 'auto', p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {sessionsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
                            <CircularProgress size={20} sx={{ color: '#701280' }} />
                        </Box>
                    ) : savedSessions.length === 0 ? (
                        <Box sx={{ textAlign: 'center', pt: 4, px: 2 }}>
                            <ChatBubbleOutlineIcon sx={{ fontSize: '2rem', color: 'rgba(0,0,0,0.15)', mb: 1 }} />
                            <Typography sx={{ fontSize: '0.78rem', color: '#9B9BA8', lineHeight: 1.5 }}>
                                {t('chat.no_saved')}
                            </Typography>
                        </Box>
                    ) : (
                        savedSessions.map(s => (
                            <Box key={s.id} onClick={() => handleLoadSession(s)}
                                sx={{
                                    p: 1.2, borderRadius: '10px', cursor: 'pointer',
                                    background: currentSessionId === s.id ? 'rgba(112,18,128,0.08)' : 'transparent',
                                    border: currentSessionId === s.id ? '1px solid rgba(112,18,128,0.2)' : '1px solid transparent',
                                    '&:hover': { background: currentSessionId === s.id ? 'rgba(112,18,128,0.1)' : 'rgba(0,0,0,0.04)' },
                                    display: 'flex', alignItems: 'flex-start', gap: 1,
                                }}>
                                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1C1C28', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {s.name}
                                    </Typography>
                                    {s.preview && (
                                        <Typography sx={{ fontSize: '0.7rem', color: '#9B9BA8', mt: 0.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {s.preview}
                                        </Typography>
                                    )}
                                </Box>
                                <IconButton size="small" onClick={(e) => handleDeleteSession(e, s.id)}
                                    sx={{ color: '#9B9BA8', p: 0.3, opacity: 0, '.MuiBox-root:hover > &': { opacity: 1 }, '&:hover': { color: '#EF4444', opacity: 1 }, flexShrink: 0 }}>
                                    <DeleteOutlineIcon sx={{ fontSize: '0.9rem' }} />
                                </IconButton>
                            </Box>
                        ))
                    )}
                </Box>
            </Box>

            {/* Main Chat Area */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

                {/* Top bar */}
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.07)', background: '#F5F4FF', display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                    <Tooltip title={sidebarOpen ? t('chat.hide_sidebar') : t('chat.show_sidebar')}>
                        <IconButton size="small" onClick={() => setSidebarOpen(v => !v)}
                            sx={{ color: '#6B6B7B', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '9px', p: 0.6, '&:hover': { color: '#701280', borderColor: 'rgba(112,18,128,0.3)', background: 'rgba(112,18,128,0.06)' } }}>
                            {sidebarOpen ? <ChevronLeftIcon sx={{ fontSize: '1rem' }} /> : <ChevronRightIcon sx={{ fontSize: '1rem' }} />}
                        </IconButton>
                    </Tooltip>

                    {/* Model switcher */}
                    <Box sx={{ display: 'flex', background: 'rgba(0,0,0,0.05)', borderRadius: '10px', p: 0.4, gap: 0.3 }}>
                        {Object.values(MODELS).map(m => {
                            const isActive = m.id === activeModelId;
                            const isLocked = m.id === 'claude' && !hasProMaxAccess;
                            return (
                                <Box
                                    key={m.id}
                                    onClick={() => switchModel(m.id)}
                                    sx={{
                                        position: 'relative',
                                        display: 'flex', alignItems: 'center', gap: 0.6,
                                        px: 1.3, py: 0.6, borderRadius: '8px', cursor: 'pointer',
                                        background: isActive ? '#fff' : 'transparent',
                                        boxShadow: isActive ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                                        transition: 'all 0.18s',
                                        opacity: isLocked ? 0.75 : 1,
                                        '&:hover': { background: isActive ? '#fff' : 'rgba(255,255,255,0.5)' },
                                    }}
                                >
                                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: m.color, flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: isActive ? 700 : 500, color: isActive ? m.color : '#6B6B7B', whiteSpace: 'nowrap', transition: 'color 0.15s' }}>
                                        {m.label}
                                    </Typography>
                                    {isLocked && (
                                        <Box sx={{
                                            fontSize: '0.6rem', fontWeight: 700, lineHeight: 1,
                                            px: 0.5, py: 0.15, borderRadius: '4px',
                                            background: 'linear-gradient(135deg, #701280, #8E3A9E)',
                                            color: '#fff', letterSpacing: '0.02em',
                                        }}>
                                            MAX
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    <Box sx={{ flex: 1 }} />

                    {messages.length > 0 && (
                        <Tooltip title={t('chat.save_tooltip')}>
                            <IconButton onClick={handleOpenSave} size="small"
                                sx={{ color: '#701280', border: '1px solid rgba(112,18,128,0.25)', borderRadius: '9px', p: 0.7, background: 'rgba(112,18,128,0.06)', '&:hover': { background: 'rgba(112,18,128,0.12)' } }}>
                                <BookmarkAddIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                        </Tooltip>
                    )}

                    <Tooltip title={t('chat.new_chat')}>
                        <IconButton onClick={handleNewChat} size="small"
                            sx={{ color: '#6B6B7B', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '9px', p: 0.7, '&:hover': { color: '#1C1C28', borderColor: 'rgba(112,18,128,0.3)', background: 'rgba(112,18,128,0.06)' } }}>
                            <AddIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Messages */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 0, '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: 4 } }}>
                    {isEmpty && (
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, maxWidth: 580, mx: 'auto', width: '100%', py: 4 }}>
                            <Box sx={{ width: 64, height: 64, borderRadius: '20px', background: 'linear-gradient(135deg, #701280, #FF6B35)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 36px rgba(112,18,128,0.35)' }}>
                                <AutoAwesomeIcon sx={{ color: '#fff', fontSize: '1.8rem' }} />
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography sx={{ color: '#1C1C28', mb: 0.5, fontSize: '1.3rem', fontWeight: 800 }}>
                                    {t('chat.empty_greeting')}
                                </Typography>
                                <Typography sx={{ color: '#6B6B7B', fontSize: '0.88rem', lineHeight: 1.6 }}>
                                    {t('chat.empty_sub').split('\n').map((line, i, arr) => (
                                        <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
                                    ))}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9, width: '100%' }}>
                                {(Array.isArray(SUGGESTIONS) ? SUGGESTIONS : []).map((s) => (
                                    <Box key={s} onClick={() => handleSend(s)}
                                        sx={{ p: 1.5, borderRadius: '12px', background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.07)', cursor: 'pointer', transition: 'all 0.18s', '&:hover': { background: 'rgba(112,18,128,0.07)', borderColor: 'rgba(112,18,128,0.22)', transform: 'translateX(3px)' } }}>
                                        <Typography sx={{ fontSize: '0.875rem', color: '#4A4A5A' }}>{s}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {messages.map((msg, i) => (
                        <Box key={i} sx={{ display: 'flex', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row', gap: 1.5, mb: 2.5, alignItems: 'flex-start' }}>
                            <Avatar sx={{ width: 32, height: 32, flexShrink: 0, fontSize: '0.78rem', fontWeight: 700, background: msg.role === 'user' ? 'linear-gradient(135deg, #701280, #8E3A9E)' : `linear-gradient(135deg, ${activeModel.color}, ${activeModel.color}aa)` }}>
                                {msg.role === 'user' ? (user?.name || 'U')[0].toUpperCase() : activeModel.icon}
                            </Avatar>
                            <Box sx={{ maxWidth: '75%' }}>
                                {msg.role === 'user' && msg._attachments && msg._attachments.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.8, justifyContent: 'flex-end' }}>
                                        {msg._attachments.map((att, ai) => (
                                            <Chip key={ai} size="small" icon={fileIcon(att.type)}
                                                label={att.name}
                                                sx={{ fontSize: '0.7rem', height: 22, background: 'rgba(112,18,128,0.12)', color: '#701280', border: '1px solid rgba(112,18,128,0.2)', '& .MuiChip-icon': { color: '#701280', fontSize: '0.8rem' } }} />
                                        ))}
                                    </Box>
                                )}
                                {msg.role === 'user' && msg.images && msg.images.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 0.8, justifyContent: 'flex-end' }}>
                                        {msg.images.map((img, ii) => (
                                            <Box key={ii} component="img" src={img.data_url} alt={img.name}
                                                sx={{ maxWidth: 200, maxHeight: 150, borderRadius: '10px', objectFit: 'cover', border: '2px solid rgba(112,18,128,0.3)' }} />
                                        ))}
                                    </Box>
                                )}
                                {(msg._displayText || (!msg._attachments && !msg.images)) && (
                                    <Box sx={{ px: 2, py: 1.5, borderRadius: msg.role === 'user' ? '18px 4px 18px 18px' : '4px 18px 18px 18px', background: msg.role === 'user' ? 'linear-gradient(135deg, #701280, #8E3A9E)' : msg.isError ? 'rgba(239,68,68,0.08)' : '#fff', border: msg.role !== 'user' ? msg.isError ? '1px solid rgba(239,68,68,0.18)' : '1px solid rgba(0,0,0,0.07)' : 'none', boxShadow: msg.role !== 'user' ? '0 1px 4px rgba(0,0,0,0.04)' : 'none', fontSize: '0.9rem', color: msg.role === 'user' ? '#fff' : msg.isError ? '#EF4444' : '#1C1C28', lineHeight: 1.65 }}>
                                        {msg.role === 'assistant' && !msg.isError
                                            ? <MarkdownText text={msg.content} />
                                            : (msg._displayText || msg.content)}
                                    </Box>
                                )}
                                {msg.role === 'assistant' && !msg.isError && (
                                    <Box sx={{ display: 'flex', mt: 0.3, ml: 0.5, gap: 0 }}>
                                        <CopyButton text={msg.content} />
                                        <ExportButton text={msg.content} />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    ))}

                    {loading && (
                        <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, alignItems: 'flex-start' }}>
                            <Avatar sx={{ width: 32, height: 32, flexShrink: 0, fontSize: '0.78rem', background: `linear-gradient(135deg, ${activeModel.color}, ${activeModel.color}aa)` }}>{activeModel.icon}</Avatar>
                            <Box sx={{ px: 2, py: 1.5, borderRadius: '4px 18px 18px 18px', background: '#fff', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ display: 'flex', gap: 0.6 }}>
                                    {[0, 1, 2].map(i => (
                                        <Box key={i} sx={{ width: 7, height: 7, borderRadius: '50%', background: activeModel.color, animation: 'pulse 1.4s ease-in-out infinite', animationDelay: `${i * 0.2}s`, opacity: 0.6, '@keyframes pulse': { '0%,80%,100%': { transform: 'scale(0.8)', opacity: 0.4 }, '40%': { transform: 'scale(1.1)', opacity: 1 } } }} />
                                    ))}
                                </Box>
                                <Typography sx={{ fontSize: '0.78rem', color: '#9B9BA8' }}>{t('chat.thinking')}</Typography>
                            </Box>
                        </Box>
                    )}
                    <div ref={messagesEndRef} />
                </Box>

                {/* Attachment preview strip */}
                {attachments.length > 0 && (
                    <Box sx={{ px: 3, pt: 1, pb: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.8, borderTop: '1px solid rgba(0,0,0,0.06)', background: '#fff', flexShrink: 0 }}>
                        {attachments.map((att, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.2, py: 0.5, borderRadius: '8px', background: att.type === 'image' ? 'rgba(112,18,128,0.08)' : 'rgba(16,163,127,0.08)', border: `1px solid ${att.type === 'image' ? 'rgba(112,18,128,0.2)' : 'rgba(16,163,127,0.2)'}` }}>
                                {att.type === 'image'
                                    ? <Box component="img" src={att.data_url} alt={att.name} sx={{ width: 28, height: 28, borderRadius: '5px', objectFit: 'cover' }} />
                                    : fileIcon(att.type)
                                }
                                <Typography sx={{ fontSize: '0.72rem', color: '#1C1C28', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.name}</Typography>
                                <IconButton size="small" onClick={() => removeAttachment(idx)} sx={{ p: 0.2, color: '#9B9BA8', '&:hover': { color: '#EF4444' } }}>
                                    <CloseIcon sx={{ fontSize: '0.75rem' }} />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Input */}
                <Box sx={{ px: 3, pb: 3, pt: attachments.length > 0 ? 0.8 : 1.5, flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', gap: 1, background: '#fff', border: '1.5px solid rgba(0,0,0,0.10)', borderRadius: '16px', p: 1, transition: 'border-color 0.2s, box-shadow 0.2s', '&:focus-within': { borderColor: 'rgba(112,18,128,0.4)', boxShadow: '0 0 0 3px rgba(112,18,128,0.08)' } }}>
                        <Tooltip title={t('chat.attach_tooltip')}>
                            <IconButton size="small" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                                sx={{ alignSelf: 'flex-end', mb: 0.5, width: 34, height: 34, color: uploading ? '#701280' : '#9B9BA8', borderRadius: '9px', flexShrink: 0, '&:hover': { color: '#701280', background: 'rgba(112,18,128,0.08)' } }}>
                                {uploading ? <CircularProgress size={14} sx={{ color: '#701280' }} /> : <AttachFileIcon sx={{ fontSize: '1rem' }} />}
                            </IconButton>
                        </Tooltip>
                        <TextField fullWidth multiline maxRows={5}
                            placeholder={t('chat.input_placeholder')}
                            value={input} onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                            variant="standard"
                            sx={{ '& .MuiInputBase-root': { background: 'transparent', fontSize: '0.92rem', color: '#1C1C28', padding: '8px 10px', '&:before, &:after': { display: 'none' } }, '& .MuiInputBase-input::placeholder': { color: '#9B9BA8', opacity: 1 } }}
                        />
                        <IconButton onClick={() => handleSend()} disabled={(!input.trim() && attachments.length === 0) || loading}
                            sx={{ alignSelf: 'flex-end', m: 0.5, width: 38, height: 38, background: (input.trim() || attachments.length > 0) && !loading ? 'linear-gradient(135deg, #701280, #8E3A9E)' : 'rgba(0,0,0,0.06)', color: (input.trim() || attachments.length > 0) && !loading ? '#fff' : '#9B9BA8', borderRadius: '10px', transition: 'all 0.2s', '&:hover': { transform: 'scale(1.05)' }, '&.Mui-disabled': { opacity: 0.5 } }}>
                            {loading ? <CircularProgress size={16} sx={{ color: '#9B9BA8' }} /> : <SendIcon sx={{ fontSize: '1rem' }} />}
                        </IconButton>
                    </Box>
                    <Typography sx={{ fontSize: '0.68rem', color: '#C0C0CA', textAlign: 'center', mt: 0.8 }}>
                        {t('chat.footer_hint')}
                    </Typography>
                </Box>
            </Box>

            {/* Save Dialog */}
            <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} PaperProps={{ sx: { borderRadius: '16px', minWidth: 360 } }}>
                <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700, pb: 1 }}>
                    {t('chat.save_dialog_title')}
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography sx={{ fontSize: '0.82rem', color: '#6B6B7B', mb: 2 }}>
                        {t('chat.save_dialog_desc')}
                    </Typography>
                    <TextField fullWidth label={t('chat.save_dialog_label')} value={saveName} onChange={(e) => setSaveName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveChat(); }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setSaveDialogOpen(false)} variant="outlined"
                        sx={{ borderRadius: '10px', textTransform: 'none', borderColor: 'rgba(0,0,0,0.15)', color: '#6B6B7B' }}>
                        {t('chat.cancel')}
                    </Button>
                    <Button onClick={handleSaveChat} variant="contained" disabled={saving || !saveName.trim()}
                        sx={{ borderRadius: '10px', textTransform: 'none', background: 'linear-gradient(135deg, #701280, #8E3A9E)', boxShadow: 'none' }}>
                        {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : t('chat.save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Upgrade modal — shown when free user clicks Claude Opus */}
            <PricingModal
                open={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                currentPlan={currentPlanKey}
                requiredPlan="pro_max"
                featureName="Opus 4-6"
                onSuccess={() => setShowUpgradeModal(false)}
            />
        </Box>
    );
};

export default ModernChat;
